import React, { useCallback, useMemo, useState } from "react";
import _ from "lodash";
import Grid from "@material-ui/core/Grid";
import FormHelperText from "@material-ui/core/FormHelperText";

import DashboardTable from "../../../../../../../components/DashboardTable/DashboardTable";
import RiskScoreConfirmationModal from "../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/RiskScoreConfirmationModal/RiskScoreConfimrationModal";
import { RiskScoreConfirmationModalContext } from "../../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/InvestmentRecommendationStep";

import usePaymentPlanEdit from "../../SavingsPlansTrade/hooks/usePaymentPlanEdit";
import useSwitchCalculations from "../../SavingsPlansTrade/hooks/useSwitchCalculations";
import AddInstrumentDialog from "../../SavingsPlansTrade/components/AddInstrumentDialog";
import { getPaymentPlanMaxStartDate, TRANSACTION_TYPE_VALUES } from "../../../../../constants";
import EmptyTableMessage from "../../../../../../RiskProfiling/components/EmptyTableMessage";
import { calculateSwitchTransactionSaldo } from "../../../../../utils";

import {SWITCH_PLAN_TABLE_STRUCTURE, SWITCH_PLAN_ROW_TYPE} from "../table/columns";
import { useSwitchPlanTableStyles } from "../table/styles";
import {buildUnifiedSwitchDataSource} from "../../shared/switchTableColumns";
import { useMPSummaryLine } from "../../shared/mpSummaryLine";

const SwitchPlanTable = ({
  portfolio,
  allInstruments,
  portfolioComponents,
  onInstrumentsChange,
  customerId,
  action,
  paymentPlanTransactionKey,
  groupInstruments,
  defaults,
  updateDefaults,
  dataService,
  isVirtual,
  isCustomerApp,
}) => {
  const tableClasses = useSwitchPlanTableStyles();
  const [activeItem, setActiveItem] = useState(undefined);
  const clientSRI = dataService.srri;

  const {
    dialogOpen,
    setDialogOpen,
    instrumentsHandlers,
    isAnlageberatung,
    handleChange,
    handleAmountChange,
    handlePlanDataChange,
    handleAddItems,
    handleAddSavingPlanButtonClick,
    onRemoveClick,
    onRemoveAllClick,
    riskModalProps,
  } = usePaymentPlanEdit({
    portfolio,
    allInstruments,
    onInstrumentsChange,
    customerId,
    action,
    groupInstruments,
    defaults,
    updateDefaults,
    dataService,
    transactionType: TRANSACTION_TYPE_VALUES.SWITCH_PLAN,
    isVirtual,
    clientSRI,
  });

  // For edit/delete, use payment_id as unique key (multiple plans per ISIN possible).
  // For create, use _id (stable unique ID assigned at creation).
  const getItemId = useCallback(
    (item) => action === "create" ? `create_${item._id}` : item.data.payment_id,
    [action]
  );

  // Build a map of switchOutItemId -> switchOutItem for options
  const switchOutsMap = useMemo(() => {
    const map = {};
    (groupInstruments || []).forEach((item) => {
      if (item && item.data) {
        map[getItemId(item)] = item;
      }
    });
    return map;
  }, [groupInstruments, getItemId]);

  const {
    handleSwitchAmountEuroChanged,
    handleSwitchPercentageChanged,
    handleSwitchDiscountChange,
    handleDistributeQuantitiesClick,
  } = useSwitchCalculations(handleChange, portfolio, switchOutsMap);

  // Build the flat data source for the unified table
  const switchDataSource = useMemo(
    () => buildUnifiedSwitchDataSource(groupInstruments, { hideSwitchIns: action === "delete", getItemId }),
    [groupInstruments, action, getItemId]
  );

  const { dataSource, expandSummaryLineChange, checkExpanded } = useMPSummaryLine(portfolio, switchDataSource);

  const handleClose = () => {
    setActiveItem(undefined);
  };

  const handleRemoveClick = (item) => {
    if (item.rowType === SWITCH_PLAN_ROW_TYPE.SWITCH_IN) {
      const switchOutItem = switchOutsMap[item.switchOutItemId];
      onRemoveClick(item, switchOutItem);
    } else {
      onRemoveClick(item);
    }
  };

  return (
    <RiskScoreConfirmationModalContext.Provider
      value={{
        ...riskModalProps,
        singleInvestmentEnabled: _.isEmpty(portfolio.transactions.switch_plan),
      }}
    >
      <div className={tableClasses.tableContainer}>
        <DashboardTable
          tableLayout="auto"
          dataSource={dataSource}
          structure={SWITCH_PLAN_TABLE_STRUCTURE}
          tableClasses={tableClasses}
          emptyDataMessage={<EmptyTableMessage isMPorPI={portfolio.isModelportfolio} />}
          withFooter={true}
          expanded
          withInputs
          options={{
            paymentPlanTransactionKey,
            isVirtual,
            isModelportfolio: portfolio.isModelportfolio,
            action,
            maxQtyDecimals: portfolio.maxQtyDecimals,
            // Plan-specific options
            rotationOptions: portfolio.isModelportfolio
              ? portfolio.availableMPSwitchPlanPeriodicPlans
              : portfolio.availableSwitchPlanPeriodicPlans,
            fromDateOptions: portfolio.isModelportfolio
              ? portfolio.availableMPSwitchPlanExecutionDates
              : portfolio.availableSwitchPlanExecutionDates,
            tillDateEnabled: portfolio.ordering_switch_plan_till_date_enabled,
            deleteDateEnabled: portfolio.ordering_switch_plan_delete_date_enabled,
            maxStartDate: (item) => getPaymentPlanMaxStartDate(item, portfolio, action),
            handlePlanDataChange,
            // Amount handlers
            handleAmountChange,
            handleSwitchAmountEuroChanged,
            handleSwitchPercentageChanged,
            handleDiscountChange: handleSwitchDiscountChange,
            handleDistributeQuantitiesClick,
            // Remove handlers
            handleRemoveClick,
            onRemoveAllClick,
            // Add switch-in handler
            handleSwitchInAddClick: setActiveItem,
            addButtonText: action === "create" ? "Produkte zum Tausch auswählen" : "Tauschplan auswählen",
            handleAddClick: () => handleAddSavingPlanButtonClick(action),
            // Shared options
            switchOutsMap,
            expandSummaryLineChange,
            checkExpanded,
            discountsDisabled: !_.get(portfolio, "ordering_discounts_allowed", []).includes("switch_plan"),
            availableDiscounts: portfolio.availableDiscounts,
            combinedDiscounts: portfolio.combinedDiscounts,
            depotDiscount: portfolio.discount,
            isAnlageberatung,
            clientSRRI: clientSRI,
            getTransactionSaldo: (switchOutItem) =>
              calculateSwitchTransactionSaldo(switchOutItem, portfolio.maxQtyDecimals),
          }}
        />
      </div>

      {/* Per-group switch-in errors below the table */}
      {groupInstruments &&
        groupInstruments.some((item) => item && item.errors && item.errors.buy) && (
          <Grid container>
            {groupInstruments.map(
              (item, idx) =>
                item &&
                item.errors &&
                item.errors.buy && (
                  <Grid item key={idx}>
                    <FormHelperText error>{item.errors.buy}</FormHelperText>
                  </Grid>
                )
            )}
          </Grid>
        )}

      {/* Dialog for adding switch-in instruments to a specific switch-out item */}
      {!!activeItem && (
        <AddInstrumentDialog
          open={true}
          onClose={handleClose}
          portfolio={portfolio}
          portfolioComponents={(portfolioComponents || []).filter(
            (i) => !!i.isin && i.isin !== activeItem.data.isin
          )}
          allInstruments={[activeItem, ...(activeItem.buy || [])]}
          instrumentsHandler={instrumentsHandlers["create"]}
          handleAddItems={async (targetAction, assets) => {
            await handleAddItems(targetAction, assets, activeItem);
            handleClose();
          }}
          action={action}
          withSrri={isAnlageberatung}
          clientSRRI={clientSRI}
          customerId={customerId}
          isCustomerApp={isCustomerApp}
          isVirtual={isVirtual}
        />
      )}

      {/* Dialog for adding new switch-out instruments */}
      <AddInstrumentDialog
        open={dialogOpen}
        title={action !== "create" ? "Tauschplan auswählen" : undefined}
        onClose={() => setDialogOpen(false)}
        portfolio={portfolio}
        portfolioComponents={(portfolioComponents || []).filter(
          (i) => !!i.isin && (action !== "create" || i.quantity > 0)
        )}
        allInstruments={allInstruments}
        instrumentsHandler={instrumentsHandlers[action]}
        handleAddItems={handleAddItems}
        action={action}
        withSrri={isAnlageberatung}
        clientSRRI={clientSRI}
        customerId={customerId}
        isCustomerApp={isCustomerApp}
        isVirtual={isVirtual}
        portfolioOnly
      />

      <RiskScoreConfirmationModal />
    </RiskScoreConfirmationModalContext.Provider>
  );
};

export default SwitchPlanTable;
